/**
 * AdminEmailHealthPage — E-Mail-Gesundheit (Jul-2026, Go-Live).
 * Zustellstatus, Fehlerquote, Trend & letzte Fehlversande aus email_logs.
 * Route: /admin/email-health (admin only)
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, ArrowLeft, RefreshCw, Check, AlertTriangle, CloudOff, ShieldAlert } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";

const fmtDt = (iso) => { try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso || "—"; } };

const KPI = ({ label, value, tone, testId }) => (
    <div className={`border rounded-sm p-4 ${tone}`} data-testid={testId}>
        <div className="text-[11px] uppercase tracking-wider font-bold opacity-70">{label}</div>
        <div className="text-3xl font-black font-mono-num mt-1">{value}</div>
    </div>
);

export default function AdminEmailHealthPage() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const r = await api.get("/admin/email-health");
            setData(r.data);
        } catch { /* 403 → redirect below */ }
        setBusy(false);
    }, []);

    useEffect(() => {
        if (loading) return;
        if (!user || user.role !== "admin") { navigate("/"); return; }
        load();
        const t = setInterval(load, 60000);
        return () => clearInterval(t);
    }, [loading, user, navigate, load]);

    if (!data) return <div className="max-w-6xl mx-auto px-4 py-10 text-gray-500">Lade E-Mail-Gesundheit…</div>;
    const c7 = data.counts.last_7d, c24 = data.counts.last_24h;

    return (
        <div className="max-w-6xl mx-auto px-4 py-8" data-testid="admin-email-health-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <Link to="/admin" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-gray-800" data-testid="email-health-back">
                        <ArrowLeft className="w-3.5 h-3.5" /> Admin-Dashboard
                    </Link>
                    <h1 className="font-display text-3xl font-black mt-1 flex items-center gap-2"><Mail className="w-7 h-7" /> E-Mail-Gesundheit</h1>
                </div>
                <Button onClick={load} disabled={busy} variant="outline" className="rounded-sm" data-testid="email-health-refresh">
                    <RefreshCw className={`w-4 h-4 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Aktualisieren
                </Button>
            </div>

            {/* Status-Banner */}
            {!data.resend_configured ? (
                <div className="mb-4 bg-red-50 border-2 border-red-400 rounded-sm p-3 text-sm text-red-800 flex items-center gap-2" data-testid="email-health-no-key">
                    <CloudOff className="w-4 h-4" /> Kein RESEND_API_KEY — alle Mails werden nur geloggt (Mock).
                </div>
            ) : data.domain_restricted ? (
                <div className="mb-4 bg-amber-50 border-2 border-amber-400 rounded-sm p-3 text-sm text-amber-900 flex items-center gap-2" data-testid="email-health-domain-warn">
                    <ShieldAlert className="w-4 h-4 shrink-0" />
                    <span><b>Resend-Testmodus erkannt:</b> Mails an fremde Empfänger werden abgelehnt. Domain unter resend.com/domains verifizieren und Absender ({data.sender_email}) auf die eigene Domain umstellen.</span>
                </div>
            ) : (
                <div className="mb-4 bg-emerald-50 border border-emerald-300 rounded-sm p-3 text-sm text-emerald-800 flex items-center gap-2" data-testid="email-health-ok">
                    <Check className="w-4 h-4" /> Versand aktiv — Absender: <b>{data.sender_email}</b>
                </div>
            )}

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <KPI label="Gesendet (7 Tage)" value={c7.sent} tone="bg-emerald-50 border-emerald-200 text-emerald-900" testId="email-health-kpi-sent" />
                <KPI label="Fehlgeschlagen (7 Tage)" value={c7.failed} tone={c7.failed > 0 ? "bg-red-50 border-red-300 text-red-900" : "bg-white border-gray-200 text-gray-800"} testId="email-health-kpi-failed" />
                <KPI label="Fehlerquote (7 Tage)" value={`${data.failure_rate_7d} %`} tone={data.failure_rate_7d > 5 ? "bg-red-50 border-red-300 text-red-900" : "bg-white border-gray-200 text-gray-800"} testId="email-health-kpi-rate" />
                <KPI label="Letzte 24 h" value={c24.total} tone="bg-white border-gray-200 text-gray-800" testId="email-health-kpi-24h" />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                {/* Fehlgeschlagene Mails */}
                <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="email-health-failed-list">
                    <h2 className="font-display font-black text-base mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-600" /> Letzte Fehlversande</h2>
                    {data.last_failed.length === 0 ? (
                        <p className="text-sm text-gray-500">Keine fehlgeschlagenen Mails. ✔</p>
                    ) : (
                        <ul className="divide-y divide-gray-100">
                            {data.last_failed.map((m, i) => (
                                <li key={i} className="py-2">
                                    <div className="text-sm font-bold truncate">{m.subject || "(ohne Betreff)"}</div>
                                    <div className="text-[11px] text-gray-500">an {m.to} · {fmtDt(m.created_at)}{m.branch_site_code ? ` · ${m.branch_site_code}` : ""}</div>
                                    <div className="text-[11px] text-red-700 mt-0.5 break-words">{m.error}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Trend + letzte Mails */}
                <div className="space-y-4">
                    <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="email-health-daily">
                        <h2 className="font-display font-black text-base mb-2">Tages-Trend (14 Tage)</h2>
                        {data.daily.length === 0 ? <p className="text-sm text-gray-500">Noch keine Daten.</p> : (
                            <table className="w-full text-xs">
                                <thead><tr className="text-left text-gray-500 uppercase tracking-wider">
                                    <th className="py-1">Datum</th><th className="text-right">Gesendet</th><th className="text-right">Fehler</th><th className="text-right">Mock</th>
                                </tr></thead>
                                <tbody>
                                    {data.daily.map((d) => (
                                        <tr key={d.date} className="border-t border-gray-100 font-mono-num">
                                            <td className="py-1">{d.date}</td>
                                            <td className="text-right text-emerald-700 font-bold">{d.sent}</td>
                                            <td className={`text-right font-bold ${d.failed > 0 ? "text-red-600" : "text-gray-400"}`}>{d.failed}</td>
                                            <td className="text-right text-gray-400">{d.mock}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                    <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="email-health-recent">
                        <h2 className="font-display font-black text-base mb-2">Zuletzt versendet / geloggt</h2>
                        <ul className="divide-y divide-gray-100">
                            {data.recent.map((m, i) => (
                                <li key={i} className="py-1.5 flex items-center gap-2 text-xs">
                                    <span className={`shrink-0 inline-block w-2 h-2 rounded-full ${m.status === "sent" ? "bg-emerald-500" : m.status === "failed" ? "bg-red-500" : "bg-gray-300"}`} />
                                    <span className="flex-1 truncate">{m.subject || "(ohne Betreff)"} <span className="text-gray-400">→ {m.to}</span></span>
                                    <span className="text-gray-400 shrink-0">{fmtDt(m.created_at)}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
            <p className="mt-4 text-[11px] text-gray-400">Hinweis: Bounces nach erfolgreicher Übergabe an Resend erscheinen hier nicht automatisch (dafür wären Resend-Webhooks nötig) — „Fehlgeschlagen" umfasst alle vom Provider abgelehnten Sendungen.</p>
        </div>
    );
}
